import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Save, Trash2, MapPin, Sparkles, Loader2, X } from 'lucide-react';
import { API, adminAuthHeader, resolveImg } from '../api';
import ImageUploader from './ImageUploader';
import AdminSearch from './AdminSearch';

const emptyForm = { type: 'destination', name: '', slug: '', image: '', fb_type: 'default', order: 0 };
const FB_TYPES = ['default', 'beach', 'mountain', 'city', 'family', 'couple', 'temple', 'wildlife', 'adventure'];

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-_]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const TaxonomyManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('destination');
  const [q, setQ] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/taxonomies`);
      setItems(res.data || []);
    } catch (_) { setItems([]); } finally { setLoading(false); }
  };

  const start = (t) => {
    setErr('');
    if (t) {
      setEditing(t.id);
      setForm({ ...emptyForm, ...t });
    } else {
      setEditing('new');
      setForm({ ...emptyForm, type: tab });
    }
  };
  const cancel = () => { setEditing(null); setForm(emptyForm); setErr(''); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setErr('');
    if (!form.name.trim()) return setErr('Name is required');
    if (!['destination', 'theme'].includes(form.type)) return setErr('Type must be destination or theme');
    const payload = { ...form, slug: (form.slug || slugify(form.name)), order: Number(form.order) || 0 };
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/taxonomies`, payload, { headers: adminAuthHeader() });
      } else {
        await axios.put(`${API}/admin/taxonomies/${editing}`, payload, { headers: adminAuthHeader() });
      }
      await load();
      cancel();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Packages linked to it will keep their slug — you can rename them later.`)) return;
    try {
      await axios.delete(`${API}/admin/taxonomies/${id}`, { headers: adminAuthHeader() });
      await load();
    } catch (_) {}
  };

  const filtered = items.filter((t) => t.type === tab).filter((t) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [t.name, t.slug, t.fb_type].filter(Boolean).join(' ').toLowerCase().includes(s);
  });
  const Icon = tab === 'destination' ? MapPin : Sparkles;

  return (
    <div data-testid="taxonomy-manager">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Destinations &amp; Themes</h2>
          <p className="text-sm text-gray-500">Manage the destinations and themes shown across the site and inside the package editor.</p>
        </div>
        <div className="inline-flex bg-white border border-gray-200 rounded-md p-1">
          {[
            { k: 'destination', label: 'Destinations' },
            { k: 'theme', label: 'Themes' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => { setTab(t.k); cancel(); }}
              data-testid={`tax-tab-${t.k}`}
              className={`px-3 py-1.5 text-sm rounded ${tab === t.k ? 'bg-[#0d64a8] text-white' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              {t.label} ({items.filter((x) => x.type === t.k).length})
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <p className="text-xs text-gray-500">
          {tab === 'destination'
            ? 'Destinations feed the package editor dropdown and the "Top Trending Countries" and "Trending Indian States" carousels.'
            : 'Themes feed the "Explore Destinations by Theme" carousel and the package theme picker.'}
        </p>
        <button onClick={() => start(null)} data-testid="tax-add-btn" className="flex items-center gap-1.5 bg-[#0d64a8] text-white text-sm px-4 py-2 rounded-md">
          <Plus size={14} /> Add {tab === 'destination' ? 'Destination' : 'Theme'}
        </button>
      </div>

      <div className="mb-4">
        <AdminSearch value={q} onChange={setQ} placeholder={`Search ${tab === 'destination' ? 'destinations' : 'themes'} by name, slug, type...`} testId="tax-search" />
      </div>


      {editing !== null && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4" data-testid="tax-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              {editing === 'new' ? `New ${form.type}` : `Editing "${form.name}"`}
            </h3>
            <button onClick={cancel} className="text-gray-400 hover:text-gray-700"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
                <select value={form.type} onChange={(e) => upd('type', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]" data-testid="tax-type-select">
                  <option value="destination">Destination</option>
                  <option value="theme">Theme</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input value={form.name} onChange={(e) => upd('name', e.target.value)} placeholder="e.g. Bali or Honeymoon" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="tax-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Slug <span className="text-gray-400">(auto if left blank)</span></label>
                <input value={form.slug} onChange={(e) => upd('slug', e.target.value)} placeholder={slugify(form.name)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="tax-slug-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fallback Theme</label>
                  <select value={form.fb_type} onChange={(e) => upd('fb_type', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]">
                    {FB_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Order</label>
                  <input type="number" value={form.order} onChange={(e) => upd('order', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" />
                </div>
              </div>
            </div>
            <div className="md:col-span-2">
              <ImageUploader value={form.image} onChange={(url) => upd('image', url)} label="Cover Image" hint="Used on home page carousel cards. 800×500 or larger works best." height="h-40" />
            </div>
          </div>
          {err && <div className="mt-3 bg-red-50 border border-red-100 text-red-700 text-sm px-3 py-2 rounded-md">{err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={cancel} className="text-sm text-gray-600 px-4 py-2 border border-gray-200 rounded-md">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="tax-save-btn" className="text-sm bg-[#0d64a8] hover:bg-[#0a548e] text-white px-4 py-2 rounded-md flex items-center gap-1.5">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14} />} {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-sm text-gray-500 py-10">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <Icon className="mx-auto text-gray-300 mb-2" size={38}/>
          <p className="text-gray-700 font-medium">No {tab}s yet</p>
          <button onClick={() => start(null)} className="mt-3 bg-[#0d64a8] text-white text-sm px-4 py-2 rounded-md">Add your first {tab}</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`tax-card-${t.slug}`} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-sm transition flex flex-col">
              <div className="aspect-video bg-gray-50 relative">
                {t.image ? (
                  <img src={resolveImg(t.image)} alt={t.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-300"><Icon size={28}/></div>
                )}
                <span className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest text-gray-700 border border-white/60">
                  {t.type}
                </span>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="font-semibold text-sm text-gray-900 truncate">{t.name}</div>
                <div className="text-[11px] text-gray-500 truncate">/{t.slug}</div>
                <div className="mt-auto pt-2 flex gap-2">
                  <button onClick={() => start(t)} data-testid={`tax-edit-${t.slug}`} className="text-[11px] text-[#0d64a8] hover:underline">Edit</button>
                  <button onClick={() => remove(t.id, t.name)} data-testid={`tax-delete-${t.slug}`} className="text-[11px] text-red-500 hover:underline">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TaxonomyManager;
